import React, { useState } from 'react';
import { TripMember, TripMeta } from '../../types/trip';
import { useDesign } from '../../designs';

interface CrewClaimScreenProps {
  meta: TripMeta;
  members: Record<string, TripMember>;
  onClaimExisting: (memberUid: string) => Promise<void>;
  onClaimNew: (displayName: string) => Promise<void>;
}

/**
 * Interstitial screen shown to anonymous share-link visitors who
 * have not yet claimed a crew-member identity for this trip.
 *
 * Layout: occasion → title → dates → diamond divider → "WHO ARE YOU?" → name cards
 * Follows the Parlor design language using design tokens from DesignProvider.
 */
export default function CrewClaimScreen({
  meta,
  members,
  onClaimExisting,
  onClaimNew,
}: CrewClaimScreenProps) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const displayFont = `'${design.typography.displayFont}', serif`;
  const borderRadius = `${design.options.cardBorderRadius}px`;

  const [showNameInput, setShowNameInput] = useState(false);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);

  // All planner-created members (planners + crew placeholders), not anonymous visitors.
  const claimableMembers = Object.entries(members).filter(
    ([, m]) => !m.isAnonymous,
  );

  const hasClaimableNames = claimableMembers.length > 0;

  // Format date range like the hero: "May 15 – May 18"
  const formatDates = () => {
    if (!meta.startDate || !meta.endDate) return '';
    const start = new Date(meta.startDate + 'T00:00:00');
    const end = new Date(meta.endDate + 'T00:00:00');
    const fmt = (dt: Date) => dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    return `${fmt(start)} – ${fmt(end)}`;
  };

  const handleClaim = async (memberUid: string) => {
    if (loading) return;
    setLoading(true);
    try {
      await onClaimExisting(memberUid);
    } catch {
      setLoading(false);
    }
  };

  const handleNewName = async () => {
    if (loading || !newName.trim()) return;
    setLoading(true);
    try {
      await onClaimNew(newName.trim());
    } catch {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: d.bgPrimary,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 26px',
        fontFamily: "'Raleway', sans-serif",
      }}
    >
      {/* KREWE watermark */}
      <div
        style={{
          fontSize: fs.wordmark,
          fontWeight: 800,
          letterSpacing: '9px',
          textTransform: 'uppercase',
          color: 'rgba(200,146,58,0.18)',
          marginBottom: '32px',
        }}
      >
        KREWE
      </div>

      {/* Occasion line */}
      {meta.occasion && (
        <div
          style={{
            fontFamily: "'Raleway', sans-serif",
            fontSize: fs.sectionHeader,
            fontWeight: 700,
            letterSpacing: '3.5px',
            textTransform: 'uppercase',
            color: d.accent,
            marginBottom: '10px',
          }}
        >
          {meta.occasion}
        </div>
      )}

      {/* Trip name */}
      <h2
        style={{
          fontFamily: displayFont,
          fontSize: '32px',
          fontWeight: 700,
          color: d.textPrimary,
          textAlign: 'center',
          margin: '0 0 8px',
          lineHeight: 1.0,
        }}
      >
        {meta.tripName}
      </h2>

      {/* Dates */}
      {formatDates() && (
        <div
          style={{
            fontFamily: displayFont,
            fontSize: '15px',
            fontWeight: 600,
            fontStyle: 'italic',
            color: d.textSecondary,
            marginBottom: '24px',
          }}
        >
          {formatDates()}
        </div>
      )}

      {/* Diamond divider */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px',
        }}
      >
        <div style={{ width: '40px', height: '1px', backgroundColor: d.ornamentLine }} />
        <span style={{ fontSize: '8px', color: d.ornament }}>&#9670;</span>
        <div style={{ width: '40px', height: '1px', backgroundColor: d.ornamentLine }} />
      </div>

      {/* Instruction line */}
      <p
        style={{
          fontFamily: "'Raleway', sans-serif",
          fontSize: fs.bodyInput,
          fontWeight: 300,
          color: d.accent,
          margin: '0 0 28px',
          textAlign: 'center',
        }}
      >
        Tap your name to jump in.
      </p>

      {/* Name cards — planners + crew */}
      {hasClaimableNames && (
        <div style={{ width: '100%', maxWidth: '340px', marginBottom: '12px' }}>
          {claimableMembers.map(([uid, member]) => (
            <button
              key={uid}
              onClick={() => handleClaim(uid)}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '16px',
                marginBottom: '12px',
                backgroundColor: d.bgSurface,
                border: 'none',
                borderLeft: `3px solid ${d.accent}`,
                borderRadius,
                cursor: loading ? 'not-allowed' : 'pointer',
                textAlign: 'left',
                transition: 'background-color 0.15s',
                opacity: loading ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = d.bgElevated;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = d.bgSurface;
              }}
            >
              <span
                style={{
                  fontFamily: displayFont,
                  fontSize: '18px',
                  fontWeight: 600,
                  color: d.textPrimary,
                }}
              >
                {member.displayName}
              </span>
              {member.role === 'planner' && (
                <span
                  style={{
                    fontFamily: "'Raleway', sans-serif",
                    fontSize: fs.sectionHeader,
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    color: d.accent,
                  }}
                >
                  Planner
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* "Add my name" dashed card — or direct entry when no crew names */}
      <div style={{ width: '100%', maxWidth: '340px' }}>
        {hasClaimableNames && !showNameInput && (
          <button
            onClick={() => setShowNameInput(true)}
            style={{
              display: 'block',
              width: '100%',
              padding: '16px',
              backgroundColor: 'transparent',
              border: `1px dashed ${d.textSecondary}`,
              borderRadius,
              fontFamily: "'Raleway', sans-serif",
              fontSize: fs.bodyInput,
              fontWeight: 400,
              color: d.textSecondary,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            + Add my name
          </button>
        )}

        {/* Name input — always visible if no crew names, or when expanded */}
        {(showNameInput || !hasClaimableNames) && (
          <div
            style={{
              padding: '16px',
              backgroundColor: showNameInput ? 'transparent' : 'transparent',
              border: hasClaimableNames ? `1px dashed ${d.textSecondary}` : 'none',
              borderRadius,
            }}
          >
            {!hasClaimableNames && (
              <p
                style={{
                  fontFamily: "'Raleway', sans-serif",
                  fontSize: fs.detail,
                  fontWeight: 300,
                  color: d.textSecondary,
                  textAlign: 'center',
                  margin: '0 0 16px',
                }}
              >
                Enter your name to join this trip.
              </p>
            )}
            <input
              type="text"
              placeholder="Your name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleNewName()}
              autoFocus
              style={{
                width: '100%',
                padding: '12px 14px',
                backgroundColor: d.bgSurface,
                border: `1px solid ${d.border}`,
                borderRadius,
                fontFamily: "'Raleway', sans-serif",
                fontSize: fs.body,
                fontWeight: 400,
                color: d.textPrimary,
                outline: 'none',
                boxSizing: 'border-box',
                marginBottom: '12px',
                transition: 'border-color 0.15s',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = d.accent; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = d.border; }}
            />
            <button
              onClick={handleNewName}
              disabled={loading || !newName.trim()}
              style={{
                width: '100%',
                padding: '11px 20px',
                backgroundColor: 'transparent',
                border: `1px solid ${d.borderEmphasis}`,
                borderRadius,
                fontFamily: "'Raleway', sans-serif",
                fontSize: fs.button,
                fontWeight: 700,
                letterSpacing: '3px',
                textTransform: 'uppercase',
                color: d.accent,
                cursor: loading || !newName.trim() ? 'not-allowed' : 'pointer',
                opacity: loading || !newName.trim() ? 0.5 : 1,
              }}
            >
              <span style={{ marginRight: '6px', fontSize: '7px' }}>&#9670;</span>
              Join
              <span style={{ marginLeft: '6px', fontSize: '7px' }}>&#9670;</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
